import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAdminIncidents, useResolveIncident } from '@/hooks/useAdminCommunications';
import { formatDateTime } from '@/utils/formatters';
import { AlertTriangle, Plus, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { IncidentForm } from '../components/IncidentForm';
import { IncidentTimeline } from '../components/IncidentTimeline';

export function IncidentReportPage() {
  const [showCreate, setShowCreate] = useState(false);
  const { data, isLoading } = useAdminIncidents({});
  const resolve = useResolveIncident();
  const incidents = data?.data?.data || [];

  const severityVariant = (s: string) => s === 'critical' ? 'danger' : s === 'major' ? 'warning' : s === 'minor' ? 'info' : 'neutral';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Incidents</h1><p className="text-sm text-neutral-500">Gestion et suivi des incidents</p></div>
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => setShowCreate(true)}>Signaler un incident</Button>
      </div>

      {isLoading ? (
        <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-32" /></div>
      ) : incidents.length === 0 ? (
        <Card><CardContent><div className="flex flex-col items-center py-8"><AlertTriangle className="h-10 w-10 text-neutral-400 dark:text-neutral-600 mb-2" /><p className="text-sm text-neutral-600 dark:text-neutral-400">Aucun incident</p></div></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {incidents.map((incident) => (
            <Card key={incident.uuid}>
              <CardHeader
                title={incident.title}
                description={`Début: ${formatDateTime(incident.startedAt)}`}
                action={
                  <div className="flex items-center gap-2">
                    <Badge variant={severityVariant(incident.severity)}>{incident.severity}</Badge>
                    <Badge variant={incident.status === 'resolved' ? 'success' : 'warning'}>{incident.status}</Badge>
                    {incident.status !== 'resolved' && (
                      <Button variant="outline" size="sm" icon={<CheckCircle className="h-3 w-3" />} onClick={() => resolve.mutate(incident.uuid)}>Résoudre</Button>
                    )}
                  </div>
                }
              />
              <CardContent>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">{incident.description}</p>
                {incident.updates && incident.updates.length > 0 && <IncidentTimeline incident={incident} />}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Signaler un incident" size="lg">
        <IncidentForm onSuccess={() => setShowCreate(false)} />
      </Modal>
    </div>
  );
}
